import type {
  ChangeAdjustmentValidationErrorParams,
  CreateAdjustment,
  CreateAdjustmentFormValidationErrors,
  CreateAdjustmentRequest
} from '@library/types/members/memberAdjustments';
import { useCallback, useState } from 'react';
import {
  useCreateMemberAdjustmentMutation,
  useGetMemberPromotionReasonsQuery
} from '@bo-services/members/memberAdjustmentsApi';

export const useMemberCreateAdjustments = ({
  memberId
}: {
  memberId: string;
}) => {
  const { data = [] } = useGetMemberPromotionReasonsQuery({
    reasonType: 'adjustment'
  });

  const [
    createMemberAdjustment,
    {
      error: createMemberAdjustmentError,
      isUninitialized: isCreateMemberAdjustmentApiUninitialized,
      isLoading: isCreateMemberAdjustmentLoading,
      isError: isCreateMemberAdjustmentError,
      isSuccess: isCreateMemberAdjustmentSuccess,
      reset: resetCreateMemberAdjustmentApi
    }
  ] = useCreateMemberAdjustmentMutation();

  const [modalDisplayStatus, setModalDisplayStatus] = useState<
    'SAVE' | 'CONFIRM'
  >('SAVE');

  const defaultAdjustment = {
    adjustmentAmount: '',
    adjustmentReasonId: undefined,
    adjustmentType: undefined
  };

  const defaultAdjustmentFormValidationErrors = {
    adjustmentAmount: '',
    adjustmentReasonId: '',
    adjustmentType: ''
  };

  const [adjustment, setAdjustment] =
    useState<CreateAdjustment>(defaultAdjustment);

  const [adjustmentFormValidationErrors, setAdjustmentFormValidationErrors] =
    useState<CreateAdjustmentFormValidationErrors>(
      defaultAdjustmentFormValidationErrors
    );

  const adjustmentTypes = ['Promotion', 'Credit'];
  const isConfirmAdjustment = modalDisplayStatus === 'CONFIRM';

  const handleChangeAdjustment = useCallback(
    ({
      key,
      value
    }: {
      key: keyof CreateAdjustment;
      value: string | number | undefined;
    }) => {
      setAdjustment((prevAdjustment) => ({
        ...prevAdjustment,
        [key]: value
      }));
    },
    []
  );

  const handleResetCreateMemberAdjustment = useCallback(() => {
    if (!isCreateMemberAdjustmentApiUninitialized) {
      resetCreateMemberAdjustmentApi();
    }
  }, [
    isCreateMemberAdjustmentApiUninitialized,
    resetCreateMemberAdjustmentApi
  ]);

  const handleUpdateAdjustmentErrors = useCallback(
    ({ key, error }: ChangeAdjustmentValidationErrorParams) => {
      setAdjustmentFormValidationErrors((prevErrors) => ({
        ...prevErrors,
        [key]: error
      }));
    },
    []
  );

  const handleSaveAdjustment = useCallback(() => {
    setModalDisplayStatus('CONFIRM');
  }, []);

  const handleClickBack = useCallback(() => {
    handleResetCreateMemberAdjustment();
    setModalDisplayStatus('SAVE');
  }, [handleResetCreateMemberAdjustment]);

  const handleConfirmCreateAdjustment = useCallback(async () => {
    const { adjustmentAmount, adjustmentReasonId, adjustmentType } = adjustment;

    const createAdjustmentRequestData: CreateAdjustmentRequest = {
      adjustmentAmountInCents: Math.round(Number(adjustmentAmount) * 100),
      adjustmentReasonId: Number(adjustmentReasonId),
      isPromo: adjustmentType === 'Promotion',
      memberId
    };

    await createMemberAdjustment(createAdjustmentRequestData);
  }, [adjustment, memberId, createMemberAdjustment]);

  return {
    adjustment,
    adjustmentFormValidationErrors,
    adjustmentTypes,
    adjustmentReasonOptions: data,
    isCreateMemberAdjustmentApiUninitialized,
    createMemberAdjustmentError,
    isCreateMemberAdjustmentError,
    isCreateMemberAdjustmentLoading,
    isCreateMemberAdjustmentSuccess,
    isConfirmAdjustment,
    onClickBack: handleClickBack,
    onChangeAdjustment: handleChangeAdjustment,
    onSaveAdjustment: handleSaveAdjustment,
    onConfirmCreateAdjustment: handleConfirmCreateAdjustment,
    onResetCreateMemberAdjustmentApi: handleResetCreateMemberAdjustment,
    onUpdateAdjustmentErrors: handleUpdateAdjustmentErrors
  };
};
